import React, { useState } from "react";
import { BedDouble, Star, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SectionHeading } from "@/components/SectionHeading";
import { usePageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = [
  { icon: Sparkles, t: "5★ Luxury & Resorts", d: "Beachfront resorts, overwater villas and iconic city hotels at partner rates." },
  { icon: BedDouble, t: "4★ Premium Comfort", d: "Excellent locations and amenities without the luxury price tag." },
  { icon: Building2, t: "Business & Apart-Hotels", d: "Serviced apartments and business hotels for corporate travellers." },
  { icon: Star, t: "Boutique & Unique Stays", d: "Riads, lodges and design hotels for travellers who want character." },
];

export default function Hotels() {
  usePageMeta("Hotel Bookings", "Book handpicked hotels and resorts worldwide with exclusive rates through Makchester Tours & Travel.");
  const [f, setF] = useState({ full_name: "", phone: "", email: "", destination: "", check_in: "", check_out: "", guests: "2", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", {
        full_name: f.full_name, phone: f.phone, email: f.email,
        service_needed: "Hotel Reservation", travel_date: f.check_in,
        notes: `Hotel enquiry — Destination: ${f.destination}, Check-in: ${f.check_in}, Check-out: ${f.check_out}, Guests: ${f.guests}. ${f.notes}`,
      });
      trackEvent("enquiry_submitted", { form: "hotel" });
      toast.success("Hotel enquiry received! We'll send options shortly.");
      setF({ full_name: "", phone: "", email: "", destination: "", check_in: "", check_out: "", guests: "2", notes: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cls = "w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]";
  const lbl = "text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5 block";

  return (
    <div>
      <section className="relative py-28 bg-[#0A2E4D] overflow-hidden">
        <img src="https://images.pexels.com/photos/97083/pexels-photo-97083.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="Luxury hotel" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="mk-gold-line mb-6 inline-block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E3BA45] font-bold mb-3">Hotels</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white">Worldwide hotel reservations</h1>
          <p className="text-slate-300 mt-5 max-w-xl mx-auto">From boutique stays to 5-star icons — tell us your dates and budget, and we'll send hand-picked options.</p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {CATEGORIES.map((c) => (
              <div key={c.t} className="mk-card p-8">
                <c.icon size={28} className="text-[#D4A62A]" strokeWidth={1.5} />
                <h3 className="font-serif-display text-lg text-[#0A2E4D] mt-4">{c.t}</h3>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <SectionHeading eyebrow="Hotel Enquiry" title="Request hotel options" subtitle="We reply with 3–5 curated options matched to your budget." />
            <form onSubmit={submit} className="mk-card !transform-none p-8 grid grid-cols-2 gap-4" data-testid="hotel-enquiry-form">
              <div><label className={lbl}>Full Name</label><input required value={f.full_name} onChange={set("full_name")} className={cls} data-testid="hotel-name-input" /></div>
              <div><label className={lbl}>Phone / WhatsApp</label><input required value={f.phone} onChange={set("phone")} className={cls} data-testid="hotel-phone-input" /></div>
              <div className="col-span-2"><label className={lbl}>Email</label><input type="email" value={f.email} onChange={set("email")} className={cls} /></div>
              <div className="col-span-2"><label className={lbl}>Destination / City</label><input required value={f.destination} onChange={set("destination")} className={cls} placeholder="e.g. Dubai" data-testid="hotel-destination-input" /></div>
              <div><label className={lbl}>Check-in</label><input type="date" value={f.check_in} onChange={set("check_in")} className={cls} /></div>
              <div><label className={lbl}>Check-out</label><input type="date" value={f.check_out} onChange={set("check_out")} className={cls} /></div>
              <div><label className={lbl}>Guests</label><select value={f.guests} onChange={set("guests")} className={cls}>{[1,2,3,4,5,6,7,8].map((n) => <option key={n}>{n}</option>)}</select></div>
              <div><label className={lbl}>Preferences</label><input value={f.notes} onChange={set("notes")} className={cls} placeholder="e.g. 5★, beachfront" /></div>
              <button type="submit" disabled={busy} className="mk-btn-gold col-span-2 !py-4 disabled:opacity-60" data-testid="hotel-submit-btn">{busy ? "Sending…" : "Request Hotel Options"}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
